import Archive from "../../Archive";
import "../../Home.css";
// src/components/projects/evrald/Evrald.js
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../../context/LanguageContext";
import Navigation from "../../Navigation";
import Footer from "../../Footer";
import "./Evrald.css";

const Evrald = () => {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState("overview");
  const [isVisible, setIsVisible] = useState({
    "overview-section": true,
    "tech-stack-section": true,
    "features-section": true,
    "development-section": true,
    overview: true,
  });
  const sectionsRef = useRef({});
  const canvasRef = useRef(null);

  // Particle animation state
  const [canvasLoaded, setCanvasLoaded] = useState(false);

  // Particle animation setup
  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let animationFrameId;

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    window.addEventListener("resize", resizeCanvas);
    resizeCanvas();
    setCanvasLoaded(true);

    // Particles setup
    const particlesArray = [];
    const numberOfParticles = 70;

    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2 + 1;
        this.speedX = Math.random() * 0.5 - 0.25;
        this.speedY = Math.random() * 0.5 - 0.25;
        this.color = "#149c1c";
        this.opacity = Math.random() * 0.5 + 0.2;
      }

      update() {
        // Update position
        this.x += this.speedX;
        this.y += this.speedY;

        // Handle boundaries
        if (this.x > canvas.width) this.x = 0;
        else if (this.x < 0) this.x = canvas.width;
        if (this.y > canvas.height) this.y = 0;
        else if (this.y < 0) this.y = canvas.height;
      }

      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(20, 156, 28, ${this.opacity})`;
        ctx.fill();
      }
    }

    const init = () => {
      for (let i = 0; i < numberOfParticles; i++) {
        particlesArray.push(new Particle());
      }
    };

    const connect = () => {
      const maxDistance = 100;
      for (let a = 0; a < particlesArray.length; a++) {
        for (let b = a; b < particlesArray.length; b++) {
          const dx = particlesArray[a].x - particlesArray[b].x;
          const dy = particlesArray[a].y - particlesArray[b].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < maxDistance) {
            const opacity = 1 - distance / maxDistance;
            ctx.strokeStyle = `rgba(20, 156, 28, ${opacity * 0.3})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(particlesArray[a].x, particlesArray[a].y);
            ctx.lineTo(particlesArray[b].x, particlesArray[b].y);
            ctx.stroke();
          }
        }
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = 0; i < particlesArray.length; i++) {
        particlesArray[i].update();
        particlesArray[i].draw();
      }

      connect();
      animationFrameId = requestAnimationFrame(animate);
    };

    init();
    animate();

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, [canvasLoaded]);

  // Intersection Observer for animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.2,
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        const id = entry.target.id;
        if (entry.isIntersecting) {
          setIsVisible((prev) => ({ ...prev, [id]: true }));
        }
      });
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions
    );

    // 将当前的refs保存到一个变量中，以便在清理函数中使用
    const currentSectionRefs = { ...sectionsRef.current };

    Object.values(currentSectionRefs).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      // 使用保存的refs进行清理
      Object.values(currentSectionRefs).forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  // Text translations
  const translations = {
    backToProjects: {
      en: "Back to Projects",
      zh: "返回项目列表",
      ja: "プロジェクトに戻る",
    },
    title: {
      en: "EVRALD: Smart Business Intelligence",
      zh: "EVRALD: 智能商业数据分析系统",
      ja: "EVRALD: スマートビジネスインテリジェンス",
    },
    tagline: {
      en: "AI-driven business analytics platform",
      zh: "AI驱动的商业数据分析平台",
      ja: "AI駆動のビジネス分析プラットフォーム",
    },
    overview: {
      en: "Overview",
      zh: "概述",
      ja: "概要",
    },
    techStack: {
      en: "Tech Stack",
      zh: "技术栈",
      ja: "技術スタック",
    },
    features: {
      en: "Features",
      zh: "功能特点",
      ja: "機能",
    },
    development: {
      en: "Development",
      zh: "开发过程",
      ja: "開発プロセス",
    },
    overviewText: {
      en: "EVRALD is a cutting-edge business intelligence system designed to streamline data analysis and risk management for enterprises. The platform leverages AI to transform complex business data into actionable insights, enabling companies to reduce operational costs and increase efficiency.",
      zh: "EVRALD是一款前沿的商业智能系统，旨在简化企业的数据分析和风险管理流程。该平台利用人工智能将复杂的业务数据转化为可操作的洞察，使公司能够降低运营成本并提高效率。",
      ja: "EVRALDは企業向けデータ分析とリスク管理を効率化する最先端のビジネスインテリジェンスシステムです。このプラットフォームはAIを活用して複雑なビジネスデータを実用的な洞察に変換し、企業が運用コストを削減し効率を向上させることを可能にします。",
    },
    overviewDetail: {
      en: "The project focuses on solving trust issues in international and domestic trade while introducing AI to decision-making processes. By creating customized risk control capabilities for different enterprises, EVRALD empowers businesses with faster and more efficient decision-making tools.",
      zh: "该项目致力于解决国际和国内贸易中的信任问题，同时将AI引入决策过程。通过为不同企业创建定制化的风险控制能力，EVRALD为企业提供更快速、更高效的决策工具。",
      ja: "このプロジェクトは、国際・国内取引における信頼性の問題を解決し、意思決定プロセスにAIを導入することに焦点を当てています。さまざまな企業向けにカスタマイズされたリスク管理機能を作成することで、EVRALDは企業により迅速で効率的な意思決定ツールを提供します。",
    },
    myRole: {
      en: "My Role",
      zh: "我的角色",
      ja: "私の役割",
    },
    roleDescription: {
      en: "As the lead developer for this project, I was responsible for designing and implementing the core algorithms for data analysis and visualization. I worked closely with data scientists to integrate machine learning models and ensure high accuracy in risk assessment predictions.",
      zh: "作为该项目的首席开发人员，我负责设计和实现数据分析和可视化的核心算法。我与数据科学家紧密合作，整合机器学习模型并确保风险评估预测的高准确性。",
      ja: "このプロジェクトの主任開発者として、データ分析と可視化のための中核的なアルゴリズムの設計と実装を担当しました。データサイエンティストと緊密に協力し、機械学習モデルを統合し、リスク評価予測の高精度を確保しました。",
    },
    keyFeatures: {
      en: "Key Features",
      zh: "主要功能",
      ja: "主な機能",
    },
    featuresList: {
      en: [
        "AI-Powered Analysis: Advanced algorithms for processing large datasets",
        "Real-time Visualization: Interactive dashboards displaying key metrics",
        "Secure Data Storage: Encrypted storage with access control",
        "Risk Assessment: Predictive models for identifying potential risks",
        "EVGPT: Specialized assistant for business decision support",
      ],
      zh: [
        "AI驱动分析：处理大型数据集的高级算法",
        "实时可视化：显示关键指标的交互式仪表板",
        "安全数据存储：带访问控制的加密存储",
        "风险评估：识别潜在风险的预测模型",
        "EVGPT：专业的业务决策支持助手",
      ],
      ja: [
        "AI搭載分析：大規模データセットを処理する高度なアルゴリズム",
        "リアルタイム可視化：主要指標を表示するインタラクティブダッシュボード",
        "安全なデータストレージ：アクセス制御付き暗号化ストレージ",
        "リスク評価：潜在的リスクを特定する予測モデル",
        "EVGPT：業務意思決定支援のための専門アシスタント",
      ],
    },
    techStackTitle: {
      en: "Technology Stack",
      zh: "技术栈",
      ja: "使用技術",
    },
    frontendTech: {
      en: "Frontend",
      zh: "前端",
      ja: "フロントエンド",
    },
    backendTech: {
      en: "Backend",
      zh: "后端",
      ja: "バックエンド",
    },
    aiMlTech: {
      en: "AI/ML",
      zh: "人工智能/机器学习",
      ja: "AI/機械学習",
    },
    devopsTech: {
      en: "DevOps",
      zh: "开发运维",
      ja: "DevOps",
    },
    architecture: {
      en: "Architecture",
      zh: "架构",
      ja: "アーキテクチャ",
    },
    architectureText: {
      en: "EVRALD utilizes a microservices architecture to ensure scalability and flexibility. The system is built on containerized services orchestrated with Kubernetes, enabling seamless updates and high availability.",
      zh: "EVRALD采用微服务架构，确保可扩展性和灵活性。该系统建立在使用Kubernetes编排的容器化服务上，实现无缝更新和高可用性。",
      ja: "EVRALDはスケーラビリティと柔軟性を確保するためにマイクロサービスアーキテクチャを採用しています。このシステムはKubernetesで編成されたコンテナ化サービス上に構築されており、シームレスな更新と高可用性を実現しています。",
    },
    challenges: {
      en: "Development Challenges",
      zh: "开发挑战",
      ja: "開発課題",
    },
    challengesList: {
      en: [
        "Real-time processing of high-volume data streams",
        "Integration of diverse AI models for different business contexts",
        "Ensuring data security while maintaining accessibility",
        "Optimizing visualization performance for complex datasets",
        "Creating intuitive UI/UX for non-technical business users",
      ],
      zh: [
        "高容量数据流的实时处理",
        "为不同业务环境集成多样化的AI模型",
        "确保数据安全的同时保持可访问性",
        "优化复杂数据集的可视化性能",
        "为非技术业务用户创建直观的UI/UX",
      ],
      ja: [
        "大量データストリームのリアルタイム処理",
        "異なるビジネスコンテキスト向けの多様なAIモデルの統合",
        "アクセシビリティを維持しながらのデータセキュリティの確保",
        "複雑なデータセット向けの可視化パフォーマンスの最適化",
        "非技術系ビジネスユーザー向けの直感的なUI/UXの作成",
      ],
    },
    solutions: {
      en: "Solutions Implemented",
      zh: "实施的解决方案",
      ja: "実装したソリューション",
    },
    solutionsList: {
      en: [
        "Implemented Kafka for high-throughput data streaming",
        "Developed a modular AI framework with pluggable models",
        "Created a comprehensive security layer with role-based access control",
        "Optimized frontend rendering with WebGL for data-heavy visualizations",
        "Conducted extensive user testing to refine interface design",
      ],
      zh: [
        "实施Kafka用于高吞吐量数据流",
        "开发具有可插拔模型的模块化AI框架",
        "创建具有基于角色的访问控制的综合安全层",
        "使用WebGL优化前端渲染，用于数据密集型可视化",
        "进行广泛的用户测试以完善界面设计",
      ],
      ja: [
        "高スループットデータストリーミング用のKafkaを実装",
        "プラグ可能なモデルを持つモジュラーAIフレームワークを開発",
        "ロールベースのアクセス制御を持つ包括的なセキュリティレイヤーを作成",
        "データ量の多い可視化のためにWebGLでフロントエンドレンダリングを最適化",
        "インターフェース設計を洗練させるための広範なユーザーテストを実施",
      ],
    },
    viewOnGithub: {
      en: "View Code on GitHub",
      zh: "在GitHub上查看代码",
      ja: "GitHubでコードを見る",
    },
    visitWebsite: {
      en: "Visit Website",
      zh: "访问网站",
      ja: "ウェブサイトを訪問",
    },
  };

  // Technology stack data
  const techStack = {
    frontend: [
      { name: "React", description: "UI component library" },
      { name: "Next.js", description: "React framework" },
      { name: "TypeScript", description: "Type-safe JavaScript" },
      { name: "Tailwind CSS", description: "Utility-first CSS framework" },
      { name: "D3.js", description: "Data visualization library" },
      { name: "Three.js", description: "3D graphics library" },
    ],
    backend: [
      { name: "Node.js", description: "JavaScript runtime" },
      { name: "Express", description: "Web application framework" },
      { name: "GraphQL", description: "API query language" },
      { name: "PostgreSQL", description: "Relational database" },
      { name: "Redis", description: "In-memory data structure store" },
      { name: "Kafka", description: "Distributed event streaming platform" },
    ],
    aiml: [
      { name: "TensorFlow", description: "ML framework" },
      { name: "PyTorch", description: "Deep learning framework" },
      { name: "scikit-learn", description: "ML library" },
      { name: "Hugging Face", description: "NLP models" },
      { name: "NLTK", description: "Natural language toolkit" },
    ],
    devops: [
      { name: "Docker", description: "Container platform" },
      { name: "Kubernetes", description: "Container orchestration" },
      { name: "GitHub Actions", description: "CI/CD" },
      { name: "AWS", description: "Cloud infrastructure" },
      { name: "Prometheus", description: "Monitoring" },
      { name: "Grafana", description: "Observability platform" },
    ],
  };

  return (
    <div className="flex flex-col min-h-screen bg-white text-neutral-800">
      <Navigation activeSection="projects" />

      <div className="pt-24 pb-12 px-6">
        <div className="container mx-auto max-w-7xl">
          <div className="mb-6">
            <Link
              to="/projects"
              className="text-[#149c1c] hover:text-[#7de088] transition-colors flex items-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                  clipRule="evenodd"
                />
              </svg>
              {translations.backToProjects[language] ||
                translations.backToProjects.en}
            </Link>
          </div>

          {/* Hero Section */}
          <header className="relative mb-16 rounded-3xl overflow-hidden shadow-2xl">
            <div className="absolute inset-0 z-0">
              <canvas
                ref={canvasRef}
                className="w-full h-full bg-gradient-to-br from-white via-neutral-50 to-white"
              />
              <div className="absolute inset-0 bg-black/10"></div>
            </div>

            <div className="relative z-10 p-12 md:p-20 text-center">
              <h1 className="text-5xl md:text-7xl font-light mb-6 tracking-tight">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#7de088] to-[#149c1c]">
                  EVRALD
                </span>
              </h1>
              <h2 className="text-4xl font-light mb-8 text-neutral-800">
                {translations.title[language] || translations.title.en}
              </h2>
              <p className="max-w-3xl mx-auto text-xl text-neutral-700 mb-12 leading-relaxed">
                {translations.tagline[language] || translations.tagline.en}
              </p>
              <div className="flex justify-center space-x-4">
                <a
                  href="https://www.evrald.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#149c1c] text-white px-8 py-3 rounded-full shadow-lg hover:bg-[#0b7d14] hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                >
                  {translations.visitWebsite[language] ||
                    translations.visitWebsite.en}
                </a>
                <a
                  href="https://github.com/your-username/evrald"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-transparent text-neutral-800 border border-neutral-300 px-8 py-3 rounded-full shadow-lg hover:bg-neutral-100 hover:border-neutral-400 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                >
                  {translations.viewOnGithub[language] ||
                    translations.viewOnGithub.en}
                </a>
              </div>
            </div>
          </header>

          {/* Tabs Navigation */}
          <div className="mb-12 border-b border-neutral-200">
            <div className="flex overflow-x-auto space-x-8">
              {["overview", "techStack", "features", "development"].map(
                (tab) => (
                  <button
                    key={tab}
                    className={`py-4 px-1 font-medium text-lg whitespace-nowrap transition-colors ${
                      activeTab === tab
                        ? "text-[#149c1c] border-b-2 border-[#149c1c]"
                        : "text-neutral-500 hover:text-neutral-800"
                    }`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {translations[tab]
                      ? translations[tab][language] || translations[tab].en
                      : tab}
                  </button>
                )
              )}
            </div>
          </div>

          {/* Tab Content */}
          <div className="mb-16">
            {/* Overview Tab */}
            {activeTab === "overview" && (
              <div
                id="overview-section"
                ref={(el) => (sectionsRef.current.overview = el)}
                className="transition-all duration-500 opacity-100"
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                  <div>
                    <h3 className="text-3xl font-light mb-6 text-neutral-800">
                      {translations.overview[language] ||
                        translations.overview.en}
                    </h3>
                    <div className="prose prose-invert prose-lg max-w-none">
                      <p className="mb-6">
                        {translations.overviewText[language] ||
                          translations.overviewText.en}
                      </p>
                      <p className="mb-8">
                        {translations.overviewDetail[language] ||
                          translations.overviewDetail.en}
                      </p>

                      <h4 className="text-2xl font-light mb-4 text-neutral-800">
                        {translations.myRole[language] ||
                          translations.myRole.en}
                      </h4>
                      <p>
                        {translations.roleDescription[language] ||
                          translations.roleDescription.en}
                      </p>
                    </div>
                  </div>

                  <div>
                    <div className="bg-white p-8 rounded-2xl shadow-xl border border-neutral-200">
                      <h4 className="text-2xl font-light mb-6 text-neutral-800 border-b border-neutral-200 pb-6">
                        {translations.keyFeatures[language] ||
                          translations.keyFeatures.en}
                      </h4>
                      <ul className="space-y-4">
                        {(
                          translations.featuresList[language] ||
                          translations.featuresList.en
                        ).map((feature, index) => (
                          <li key={index} className="flex items-start">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-6 w-6 text-[#149c1c] mr-3 flex-shrink-0 mt-0.5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                            <span className="text-neutral-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tech Stack Tab */}
            {activeTab === "techStack" && (
              <div
                id="tech-stack-section"
                ref={(el) => (sectionsRef.current.techStack = el)}
                className="transition-all duration-500 opacity-100"
              >
                <h3 className="text-3xl font-light mb-10 text-neutral-800">
                  {translations.techStackTitle[language] ||
                    translations.techStackTitle.en}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-16">
                  {/* Frontend */}
                  <div className="bg-white p-8 rounded-2xl shadow-xl border border-neutral-200">
                    <div className="flex items-center mb-6">
                      <span className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center mr-4">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-6 w-6 text-blue-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                      </span>
                      <h4 className="text-2xl font-light text-neutral-800">
                        {translations.frontendTech[language] ||
                          translations.frontendTech.en}
                      </h4>
                    </div>

                    <div className="space-y-4">
                      {techStack.frontend.map((tech, index) => (
                        <div
                          key={index}
                          className="flex justify-between items-center bg-neutral-100 p-4 rounded-lg"
                        >
                          <span className="font-medium text-neutral-800">
                            {tech.name}
                          </span>
                          <span className="text-neutral-500 text-sm">
                            {tech.description}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Backend */}
                  <div className="bg-white p-8 rounded-2xl shadow-xl border border-neutral-200">
                    <div className="flex items-center mb-6">
                      <span className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center mr-4">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-6 w-6 text-green-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01"
                          />
                        </svg>
                      </span>
                      <h4 className="text-2xl font-light text-neutral-800">
                        {translations.backendTech[language] ||
                          translations.backendTech.en}
                      </h4>
                    </div>

                    <div className="space-y-4">
                      {techStack.backend.map((tech, index) => (
                        <div
                          key={index}
                          className="flex justify-between items-center bg-neutral-100 p-4 rounded-lg"
                        >
                          <span className="font-medium text-neutral-800">
                            {tech.name}
                          </span>
                          <span className="text-neutral-500 text-sm">
                            {tech.description}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* AI/ML */}
                  <div className="bg-white p-8 rounded-2xl shadow-xl border border-neutral-200">
                    <div className="flex items-center mb-6">
                      <span className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center mr-4">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-6 w-6 text-purple-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                      </span>
                      <h4 className="text-2xl font-light text-neutral-800">
                        {translations.aiMlTech[language] ||
                          translations.aiMlTech.en}
                      </h4>
                    </div>

                    <div className="space-y-4">
                      {techStack.aiml.map((tech, index) => (
                        <div
                          key={index}
                          className="flex justify-between items-center bg-neutral-100 p-4 rounded-lg"
                        >
                          <span className="font-medium text-neutral-800">
                            {tech.name}
                          </span>
                          <span className="text-neutral-500 text-sm">
                            {tech.description}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* DevOps */}
                  <div className="bg-white p-8 rounded-2xl shadow-xl border border-neutral-200">
                    <div className="flex items-center mb-6">
                      <span className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center mr-4">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-6 w-6 text-amber-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                      </span>
                      <h4 className="text-2xl font-light text-neutral-800">
                        {translations.devopsTech[language] ||
                          translations.devopsTech.en}
                      </h4>
                    </div>

                    <div className="space-y-4">
                      {techStack.devops.map((tech, index) => (
                        <div
                          key={index}
                          className="flex justify-between items-center bg-neutral-100 p-4 rounded-lg"
                        >
                          <span className="font-medium text-neutral-800">
                            {tech.name}
                          </span>
                          <span className="text-neutral-500 text-sm">
                            {tech.description}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Architecture Diagram */}
                <div className="bg-white p-8 rounded-2xl shadow-xl border border-neutral-200">
                  <h4 className="text-2xl font-light mb-6 text-neutral-800">
                    {translations.architecture[language] ||
                      translations.architecture.en}
                  </h4>
                  <p className="text-neutral-600 mb-8">
                    {translations.architectureText[language] ||
                      translations.architectureText.en}
                  </p>

                  <div className="flex justify-center p-8 bg-neutral-50 rounded-xl">
                    {/* Architecture Diagram */}
                    <div className="max-w-2xl w-full">
                      <div className="flex flex-col items-center space-y-4">
                        {/* Frontend Layer */}
                        <div className="w-full p-4 bg-blue-100 rounded-xl text-center text-blue-700 border border-blue-200">
                          <p className="font-medium">Frontend Layer</p>
                          <div className="grid grid-cols-3 gap-2 mt-2">
                            <div className="p-2 bg-blue-50 rounded-lg text-sm">
                              React
                            </div>
                            <div className="p-2 bg-blue-50 rounded-lg text-sm">
                              Next.js
                            </div>
                            <div className="p-2 bg-blue-50 rounded-lg text-sm">
                              D3.js
                            </div>
                          </div>
                        </div>

                        {/* API Gateway */}
                        <div className="w-3/4 p-3 bg-amber-100 rounded-xl text-center text-amber-700 border border-amber-200">
                          <p className="font-medium">API Gateway</p>
                        </div>

                        {/* Microservices */}
                        <div className="w-full grid grid-cols-3 gap-4">
                          <div className="p-3 bg-green-100 rounded-xl text-center text-green-700 border border-green-200">
                            <p className="font-medium">Auth Service</p>
                          </div>
                          <div className="p-3 bg-green-100 rounded-xl text-center text-green-700 border border-green-200">
                            <p className="font-medium">Analytics Engine</p>
                          </div>
                          <div className="p-3 bg-green-100 rounded-xl text-center text-green-700 border border-green-200">
                            <p className="font-medium">ML Inference</p>
                          </div>
                        </div>

                        {/* Message Broker */}
                        <div className="w-3/4 p-3 bg-purple-100 rounded-xl text-center text-purple-700 border border-purple-200">
                          <p className="font-medium">Kafka Message Broker</p>
                        </div>

                        {/* Data Layer */}
                        <div className="w-full p-4 bg-neutral-100 rounded-xl text-center text-neutral-700 border border-neutral-200">
                          <p className="font-medium">Data Storage Layer</p>
                          <div className="grid grid-cols-3 gap-2 mt-2">
                            <div className="p-2 bg-neutral-50 rounded-lg text-sm">
                              PostgreSQL
                            </div>
                            <div className="p-2 bg-neutral-50 rounded-lg text-sm">
                              Redis
                            </div>
                            <div className="p-2 bg-neutral-50 rounded-lg text-sm">
                              Object Storage
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Features Tab */}
            {activeTab === "features" && (
              <div
                id="features-section"
                ref={(el) => (sectionsRef.current.features = el)}
                className="transition-all duration-500 opacity-100"
              >
                <h3 className="text-3xl font-light mb-10 text-neutral-800">
                  {translations.features[language] || translations.features.en}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-16">
                  {/* Data Analysis Feature */}
                  <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-neutral-200 hover:shadow-2xl transition-all duration-300">
                    <div className="h-48 bg-gradient-to-br from-[#149c1c] to-[#0b7d14] p-6 flex items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-20 w-20 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                        />
                      </svg>
                    </div>
                    <div className="p-8">
                      <h4 className="text-xl font-medium mb-4 text-neutral-800">
                        Advanced Data Analysis
                      </h4>
                      <p className="text-neutral-600 mb-6">
                        Powerful algorithms process business data to identify
                        trends and anomalies, enabling informed decision-making.
                        The system handles various data types including
                        structured databases, unstructured text, and real-time
                        streams.
                      </p>
                      <div className="flex flex-wrap gap-2 mt-4">
                        <span className="px-3 py-1 bg-[#149c1c]/10 text-[#149c1c] rounded-full text-sm">
                          Predictive Analytics
                        </span>
                        <span className="px-3 py-1 bg-[#149c1c]/10 text-[#149c1c] rounded-full text-sm">
                          Real-time Processing
                        </span>
                        <span className="px-3 py-1 bg-[#149c1c]/10 text-[#149c1c] rounded-full text-sm">
                          Multi-source Integration
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Visualization Feature */}
                  <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-neutral-200 hover:shadow-2xl transition-all duration-300">
                    <div className="h-48 bg-gradient-to-br from-[#149c1c] to-[#0b7d14] p-6 flex items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-20 w-20 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <div className="p-8">
                      <h4 className="text-xl font-medium mb-4 text-neutral-800">
                        Interactive Visualizations
                      </h4>
                      <p className="text-neutral-600 mb-6">
                        Transform complex data into intuitive visualizations
                        with interactive dashboards. Users can explore data
                        through customizable charts, graphs, and 3D models to
                        uncover insights and patterns at a glance.
                      </p>
                      <div className="flex flex-wrap gap-2 mt-4">
                        <span className="px-3 py-1 bg-[#149c1c]/10 text-[#149c1c] rounded-full text-sm">
                          Real-time Dashboards
                        </span>
                        <span className="px-3 py-1 bg-[#149c1c]/10 text-[#149c1c] rounded-full text-sm">
                          3D Data Models
                        </span>
                        <span className="px-3 py-1 bg-[#149c1c]/10 text-[#149c1c] rounded-full text-sm">
                          Custom Reports
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* EVGPT Feature */}
                  <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-neutral-200 hover:shadow-2xl transition-all duration-300">
                    <div className="h-48 bg-gradient-to-br from-[#149c1c] to-[#0b7d14] p-6 flex items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-20 w-20 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                        />
                      </svg>
                    </div>
                    <div className="p-8">
                      <h4 className="text-xl font-medium mb-4 text-neutral-800">
                        EVGPT Assistant
                      </h4>
                      <p className="text-neutral-600 mb-6">
                        A specialized AI assistant trained on industry-specific
                        data to provide contextual insights and recommendations.
                        EVGPT helps decision-makers with data interpretation,
                        trend analysis, and risk assessment through natural
                        language interaction.
                      </p>
                      <div className="flex flex-wrap gap-2 mt-4">
                        <span className="px-3 py-1 bg-[#149c1c]/10 text-[#149c1c] rounded-full text-sm">
                          Natural Language Interface
                        </span>
                        <span className="px-3 py-1 bg-[#149c1c]/10 text-[#149c1c] rounded-full text-sm">
                          Industry Expertise
                        </span>
                        <span className="px-3 py-1 bg-[#149c1c]/10 text-[#149c1c] rounded-full text-sm">
                          Context-Aware
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Security Feature */}
                  <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-neutral-200 hover:shadow-2xl transition-all duration-300">
                    <div className="h-48 bg-gradient-to-br from-[#149c1c] to-[#0b7d14] p-6 flex items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-20 w-20 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                      </svg>
                    </div>
                    <div className="p-8">
                      <h4 className="text-xl font-medium mb-4 text-neutral-800">
                        Enterprise-Grade Security
                      </h4>
                      <p className="text-neutral-600 mb-6">
                        Comprehensive security measures ensure data protection
                        throughout the entire system. Features include encrypted
                        storage, role-based access control, and detailed audit
                        logging for compliance and governance.
                      </p>
                      <div className="flex flex-wrap gap-2 mt-4">
                        <span className="px-3 py-1 bg-[#149c1c]/10 text-[#149c1c] rounded-full text-sm">
                          End-to-End Encryption
                        </span>
                        <span className="px-3 py-1 bg-[#149c1c]/10 text-[#149c1c] rounded-full text-sm">
                          Access Controls
                        </span>
                        <span className="px-3 py-1 bg-[#149c1c]/10 text-[#149c1c] rounded-full text-sm">
                          Audit Trails
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Demo Showcase */}
                <div className="bg-white p-8 rounded-2xl shadow-xl border border-neutral-200 overflow-hidden">
                  <h4 className="text-2xl font-light mb-6 text-neutral-800">
                    Feature Showcase
                  </h4>
                  <div className="relative h-96 bg-neutral-100 rounded-xl overflow-hidden">
                    {/* Demo Screenshot Placeholder */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-24 w-24 text-neutral-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                      <div className="absolute text-neutral-500">
                        Demo Video Coming Soon
                      </div>
                    </div>

                    {/* Overlay with Play Button */}
                    <div className="absolute inset-0 flex items-center justify-center bg-neutral-200/50 backdrop-blur-sm">
                      <button className="bg-[#149c1c] h-16 w-16 rounded-full flex items-center justify-center shadow-xl hover:bg-[#0b7d14] transition-all duration-300 transform hover:scale-110">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-8 w-8 text-white ml-1"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Development Tab */}
            {activeTab === "development" && (
              <div
                id="development-section"
                ref={(el) => (sectionsRef.current.development = el)}
                className="transition-all duration-500 opacity-100"
              >
                <h3 className="text-3xl font-light mb-10 text-neutral-800">
                  {translations.development[language] ||
                    translations.development.en}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16">
                  {/* Development Challenges */}
                  <div className="bg-white p-8 rounded-2xl shadow-xl border border-neutral-200">
                    <h4 className="text-2xl font-light mb-6 text-neutral-800 border-b border-neutral-200 pb-4">
                      {translations.challenges[language] ||
                        translations.challenges.en}
                    </h4>
                    <ul className="space-y-3">
                      {(
                        translations.challengesList[language] ||
                        translations.challengesList.en
                      ).map((challenge, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-[#149c1c] font-medium mr-2">
                            •
                          </span>
                          <span className="text-neutral-700">{challenge}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Solutions Implemented */}
                  <div className="bg-white p-8 rounded-2xl shadow-xl border border-neutral-200">
                    <h4 className="text-2xl font-light mb-6 text-neutral-800 border-b border-neutral-200 pb-4">
                      {translations.solutions[language] ||
                        translations.solutions.en}
                    </h4>
                    <ul className="space-y-3">
                      {(
                        translations.solutionsList[language] ||
                        translations.solutionsList.en
                      ).map((solution, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-[#149c1c] font-medium mr-2">
                            •
                          </span>
                          <span className="text-neutral-700">{solution}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Development Timeline */}
                <div className="bg-white p-8 rounded-2xl shadow-xl border border-neutral-200 mb-16">
                  <h4 className="text-2xl font-light mb-8 text-neutral-800">
                    Development Timeline
                  </h4>

                  <div className="relative">
                    {/* Timeline Line */}
                    <div className="absolute left-16 top-0 bottom-0 w-1 bg-neutral-300"></div>

                    {/* Timeline Events */}
                    <div className="space-y-12">
                      {/* Event 1 */}
                      <div className="relative flex">
                        <div className="flex-shrink-0 w-16 h-16 rounded-full bg-[#149c1c] flex items-center justify-center z-10 mr-8">
                          <span className="text-white text-xl font-bold">
                            1
                          </span>
                        </div>
                        <div className="bg-neutral-100 p-6 rounded-xl">
                          <h5 className="text-xl font-medium mb-2 text-neutral-800">
                            Research & Planning
                          </h5>
                          <p className="text-neutral-700 mb-3">
                            Market research, requirement analysis, and technical
                            architecture design. Defined core features and
                            integrations.
                          </p>
                          <div className="text-neutral-500 text-sm">
                            January - February 2022
                          </div>
                        </div>
                      </div>

                      {/* Event 2 */}
                      <div className="relative flex">
                        <div className="flex-shrink-0 w-16 h-16 rounded-full bg-[#149c1c] flex items-center justify-center z-10 mr-8">
                          <span className="text-white text-xl font-bold">
                            2
                          </span>
                        </div>
                        <div className="bg-neutral-100 p-6 rounded-xl">
                          <h5 className="text-xl font-medium mb-2 text-neutral-800">
                            Core Development
                          </h5>
                          <p className="text-neutral-700 mb-3">
                            Built foundational infrastructure, database
                            architecture, and microservices. Implemented API
                            gateway and authentication systems.
                          </p>
                          <div className="text-neutral-500 text-sm">
                            March - May 2022
                          </div>
                        </div>
                      </div>

                      {/* Event 3 */}
                      <div className="relative flex">
                        <div className="flex-shrink-0 w-16 h-16 rounded-full bg-[#149c1c] flex items-center justify-center z-10 mr-8">
                          <span className="text-white text-xl font-bold">
                            3
                          </span>
                        </div>
                        <div className="bg-neutral-100 p-6 rounded-xl">
                          <h5 className="text-xl font-medium mb-2 text-neutral-800">
                            AI Integration & Frontend Development
                          </h5>
                          <p className="text-neutral-700 mb-3">
                            Integrated ML models, developed visualization
                            components, and built responsive user interfaces.
                            Created EVGPT assistant functionality.
                          </p>
                          <div className="text-neutral-500 text-sm">
                            June - August 2022
                          </div>
                        </div>
                      </div>

                      {/* Event 4 */}
                      <div className="relative flex">
                        <div className="flex-shrink-0 w-16 h-16 rounded-full bg-[#149c1c] flex items-center justify-center z-10 mr-8">
                          <span className="text-white text-xl font-bold">
                            4
                          </span>
                        </div>
                        <div className="bg-neutral-100 p-6 rounded-xl">
                          <h5 className="text-xl font-medium mb-2 text-neutral-800">
                            Testing & Optimization
                          </h5>
                          <p className="text-neutral-700 mb-3">
                            Extensive testing, performance optimization, and
                            security hardening. User acceptance testing with key
                            stakeholders and initial client feedback.
                          </p>
                          <div className="text-neutral-500 text-sm">
                            September - October 2022
                          </div>
                        </div>
                      </div>

                      {/* Event 5 */}
                      <div className="relative flex">
                        <div className="flex-shrink-0 w-16 h-16 rounded-full bg-[#149c1c] flex items-center justify-center z-10 mr-8">
                          <span className="text-white text-xl font-bold">
                            5
                          </span>
                        </div>
                        <div className="bg-neutral-100 p-6 rounded-xl">
                          <h5 className="text-xl font-medium mb-2 text-neutral-800">
                            Launch & Continuous Improvement
                          </h5>
                          <p className="text-neutral-700 mb-3">
                            Product launch with initial clients, ongoing feature
                            development, and system refinement based on user
                            feedback.
                          </p>
                          <div className="text-neutral-500 text-sm">
                            November 2022 - Present
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Case Studies */}
                <div className="bg-white p-8 rounded-2xl shadow-xl border border-neutral-200">
                  <h4 className="text-2xl font-light mb-8 text-neutral-800">
                    Results & Impact
                  </h4>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
                    <div className="bg-white p-6 rounded-xl shadow-md border border-neutral-200 transform hover:scale-105 transition-all duration-300">
                      <div className="text-6xl font-light text-[#149c1c] mb-4">
                        45%
                      </div>
                      <p className="text-neutral-800">
                        Reduction in operational costs for client companies
                      </p>
                    </div>

                    <div className="bg-white p-6 rounded-xl shadow-md border border-neutral-200 transform hover:scale-105 transition-all duration-300">
                      <div className="text-6xl font-light text-[#149c1c] mb-4">
                        25%
                      </div>
                      <p className="text-neutral-800">
                        Increase in potential profit through data-driven
                        decisions
                      </p>
                    </div>

                    <div className="bg-white p-6 rounded-xl shadow-md border border-neutral-200 transform hover:scale-105 transition-all duration-300">
                      <div className="text-6xl font-light text-[#149c1c] mb-4">
                        10x
                      </div>
                      <p className="text-neutral-800">
                        Faster data processing compared to traditional methods
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                    {/* Case Study 1 */}
                    <div className="bg-white p-8 rounded-xl shadow-md border border-neutral-200 hover:shadow-xl transition-all duration-300">
                      <div className="flex items-center justify-between mb-6">
                        <h5 className="text-xl font-medium text-neutral-800">
                          China Gas Ltd.
                        </h5>
                        <div className="bg-[#149c1c]/10 text-[#149c1c] px-3 py-1 rounded-full text-sm font-medium">
                          Case Study
                        </div>
                      </div>
                      <p className="text-neutral-700 mb-6">
                        Leading provider of natural gas and liquid petroleum gas
                        in China. EVRALD helped optimize their international
                        supply chain operations and risk management.
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="text-[#149c1c] font-semibold">
                          52% ROI within first year
                        </div>
                        <a
                          href="#"
                          className="text-neutral-800 underline hover:text-[#149c1c]"
                        >
                          Read full case study →
                        </a>
                      </div>
                    </div>

                    {/* Case Study 2 */}
                    <div className="bg-white p-8 rounded-xl shadow-md border border-neutral-200 hover:shadow-xl transition-all duration-300">
                      <div className="flex items-center justify-between mb-6">
                        <h5 className="text-xl font-medium text-neutral-800">
                          Changqing Industrial Group
                        </h5>
                        <div className="bg-[#149c1c]/10 text-[#149c1c] px-3 py-1 rounded-full text-sm font-medium">
                          Case Study
                        </div>
                      </div>
                      <p className="text-neutral-700 mb-6">
                        Major industrial enterprise in Hebei Province. EVRALD
                        enabled executives to analyze data more efficiently,
                        resulting in improved decision-making processes.
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="text-[#149c1c] font-semibold">
                          37% reduction in analysis time
                        </div>
                        <a
                          href="#"
                          className="text-neutral-800 underline hover:text-[#149c1c]"
                        >
                          Read full case study →
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Call to Action - 修改为浅色主题 */}
          <div className="bg-gradient-to-br from-[#149c1c]/20 via-white to-[#149c1c]/20 text-neutral-800 relative overflow-hidden rounded-3xl shadow-2xl">
            <div className="absolute inset-0 overflow-hidden">
              {[...Array(15)].map((_, i) => (
                <div
                  key={i}
                  className="absolute rounded-full bg-[#149c1c]/10"
                  style={{
                    width: `${Math.random() * 10 + 5}rem`,
                    height: `${Math.random() * 10 + 5}rem`,
                    top: `${Math.random() * 100}%`,
                    left: `${Math.random() * 100}%`,
                    animationDuration: `${Math.random() * 10 + 15}s`,
                    animationDelay: `${Math.random() * 5}s`,
                  }}
                ></div>
              ))}
            </div>
            <div className="relative py-16 px-8 text-center z-10">
              <h2 className="text-4xl font-light mb-6">
                Ready to transform your business with{" "}
                <span className="text-[#149c1c]">EVRALD</span>?
              </h2>
              <p className="text-xl text-neutral-700 mb-8 max-w-2xl mx-auto">
                Discover how EVRALD can transform your business data into
                actionable insights and help your company reduce costs while
                increasing efficiency.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4 max-w-xl mx-auto">
                <a
                  href="https://www.evrald.com/demo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#149c1c] text-white px-8 py-3 rounded-full shadow-lg hover:bg-[#0b7d14] hover:shadow-xl transition-all duration-300"
                >
                  Request a Demo
                </a>
                <a
                  href="https://www.evrald.com/contact"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-transparent text-neutral-800 border border-neutral-300 px-8 py-3 rounded-full shadow-lg hover:bg-neutral-100 hover:shadow-xl transition-all duration-300"
                >
                  Contact Sales
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="chain-page project-archive"><Archive project="Evrald" /></div>
      <Footer />
    </div>
  );
};

export default Evrald;
